import { message, Spin, Upload } from "antd";
import type { RcFile } from "antd/es/upload";
import type { UploadFile as AntUploadFile, UploadProps } from "antd/es/upload/interface";
import { Upload as UploadIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { uploadOssFile } from "@/api/file";
import type { OssFileUploadVO } from "@/api/file/type";
import { cn } from "@/lib/cn";

import type { UploadFileProps } from "./types";
import styles from "./UploadFile.module.css";
import { openOssFile } from "./utils";

const { Dragger } = Upload;

/** 将受控 value 规范为数组 */
const normalizeValueList = (
  value?: OssFileUploadVO | OssFileUploadVO[] | null,
): OssFileUploadVO[] => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

/** 将 OSS 上传结果转为 antd fileList（不暴露私有 url，预览/下载走签名接口） */
const toAntFileList = (list: OssFileUploadVO[]): AntUploadFile<OssFileUploadVO>[] =>
  list.map((item, index) => ({
    uid: item.objectKey || `oss-${index}`,
    name: item.originalFilename,
    status: "done",
    response: item,
  }));

/**
 * OSS 文件上传：拖拽/点击上传，对接 uploadOssFile 接口
 * 样式参考合同附件上传区块，可用于表单受控场景
 */
export const UploadFile = ({
  value,
  onChange,
  ossType = 2,
  dragHint = "点击或拖拽上传文件",
  formatHint = "支持 PDF、Word、图片格式",
  accept = ".pdf,.doc,.docx,image/*",
  maxCount,
  maxSizeBytes,
  disabled = false,
  fill = false,
  variant = "default",
  alwaysShowDropZone = false,
  hideDraggerWhenFull = false,
  className,
}: UploadFileProps) => {
  // 非受控模式下的本地上传列表
  const [internalList, setInternalList] = useState<OssFileUploadVO[]>([]);
  // 上传中的临时文件列表（展示进度）
  const [pendingFiles, setPendingFiles] = useState<AntUploadFile<OssFileUploadVO>[]>([]);
  // 预览/下载中的文件 uid
  const [actionLoadingUid, setActionLoadingUid] = useState<string | null>(null);
  /** 是否受控模式 */
  const isControlled = value !== undefined;
  // 当前已上传文件列表
  const uploadedList = useMemo(
    () => (isControlled ? normalizeValueList(value) : internalList),
    [internalList, isControlled, value],
  );
  /**
   * 多文件并发上传时 customRequest 闭包会拿到过期的 uploadedList，
   * 用 ref 同步最新列表，并在 emit 前写入，避免后完成的请求覆盖先完成的结果
   */
  const uploadedListRef = useRef(uploadedList);
  useEffect(() => {
    uploadedListRef.current = uploadedList;
  }, [uploadedList]);
  const fileList = useMemo(() => toAntFileList(uploadedList), [uploadedList]);
  // 合并已完成与上传中的文件列表
  const displayFileList = useMemo(() => {
    const doneUids = new Set(fileList.map((item) => item.uid));
    const pending = pendingFiles.filter((item) => !doneUids.has(item.uid));
    return [...fileList, ...pending];
  }, [fileList, pendingFiles]);
  // 是否存在上传中任务
  const isUploading = pendingFiles.some((item) => item.status === "uploading");
  // 全局忙碌态（上传 / 预览 / 下载）
  const isBusy = isUploading || actionLoadingUid !== null;
  // 是否单文件模式（仅显式 maxCount=1 时）
  const isSingleMode = maxCount === 1;
  // 是否已达上传上限
  const reachMaxCount = maxCount !== undefined && uploadedList.length >= maxCount;
  /** 是否展示拖拽区提示内容（单文件替换场景需始终展示） */
  const showDropContent = alwaysShowDropZone || !reachMaxCount;
  /** 单文件替换模式：不交给 antd maxCount 拦截，改由 customRequest 覆盖 */
  const draggerMaxCount = alwaysShowDropZone && isSingleMode ? undefined : maxCount;
  /** 同步受控 value */
  const emitChange = useCallback(
    (nextList: OssFileUploadVO[]) => {
      uploadedListRef.current = nextList;
      if (!isControlled) setInternalList(nextList);
      if (isSingleMode) {
        onChange?.(nextList[0]);
        return;
      }
      onChange?.(nextList.length ? nextList : []);
    },
    [isControlled, isSingleMode, onChange],
  );

  /** 更新上传中文件进度 */
  const updatePendingFile = useCallback(
    (uid: string, patch: Partial<AntUploadFile<OssFileUploadVO>>) => {
      setPendingFiles((prev) =>
        prev.map((item) => (item.uid === uid ? { ...item, ...patch } : item)),
      );
    },
    [],
  );

  /** 自定义 OSS 上传 */
  const customRequest = useCallback<NonNullable<UploadProps["customRequest"]>>(
    async (options) => {
      const { file, onSuccess, onError, onProgress } = options;
      const rcFile = file as RcFile;
      // 加入上传中列表
      setPendingFiles((prev) => [
        ...prev,
        {
          uid: rcFile.uid,
          name: rcFile.name,
          status: "uploading",
          percent: 0,
        },
      ]);
      try {
        onProgress?.({ percent: 20 });
        updatePendingFile(rcFile.uid, { percent: 20 });
        const res = await uploadOssFile({ file: rcFile, type: ossType });
        onProgress?.({ percent: 100 });
        updatePendingFile(rcFile.uid, { percent: 100 });
        onSuccess?.(res.data);
        setPendingFiles((prev) => prev.filter((item) => item.uid !== rcFile.uid));
        const currentList = uploadedListRef.current;
        const nextList = isSingleMode
          ? [res.data]
          : maxCount === undefined
            ? [...currentList, res.data]
            : [...currentList, res.data].slice(0, maxCount);
        emitChange(nextList);
      } catch (error) {
        updatePendingFile(rcFile.uid, { status: "error", percent: 0 });
        onError?.(error as Error);
      }
    },
    [emitChange, isSingleMode, maxCount, ossType, updatePendingFile],
  );

  /** 上传前校验大小与数量 */
  const beforeUpload = useCallback(
    (file: File) => {
      if (maxSizeBytes && file.size > maxSizeBytes) {
        message.error(`文件大小不能超过 ${Math.round(maxSizeBytes / 1024 / 1024)}MB`);
        return Upload.LIST_IGNORE;
      }
      if (
        !alwaysShowDropZone &&
        maxCount !== undefined &&
        !isSingleMode &&
        uploadedList.length >= maxCount
      ) {
        return Upload.LIST_IGNORE;
      }
      return true;
    },
    [alwaysShowDropZone, isSingleMode, maxCount, maxSizeBytes, uploadedList.length],
  );

  /** 移除已上传或上传失败的文件 */
  const handleRemove = useCallback(
    (file: AntUploadFile<OssFileUploadVO>) => {
      if (file.status === "uploading") return false;
      if (!file.response) {
        setPendingFiles((prev) => prev.filter((item) => item.uid !== file.uid));
        return true;
      }
      const nextList = uploadedList.filter((item) => item.objectKey !== file.response?.objectKey);
      emitChange(nextList);
      return true;
    },
    [emitChange, uploadedList],
  );

  /** 预览：获取 inline 签名 URL 后打开 */
  const handlePreview = useCallback(
    async (file: AntUploadFile<OssFileUploadVO>) => {
      const ossFile = file.response;
      if (!ossFile || actionLoadingUid) return;
      setActionLoadingUid(file.uid);
      const hide = message.loading("正在获取预览链接...", 0);
      try {
        await openOssFile(ossFile, "inline");
      } finally {
        hide();
        setActionLoadingUid(null);
      }
    },
    [actionLoadingUid],
  );

  /** 下载：获取 attachment 签名 URL 后打开 */
  const handleDownload = useCallback(
    async (file: AntUploadFile<OssFileUploadVO>) => {
      const ossFile = file.response;
      if (!ossFile || actionLoadingUid) return;
      setActionLoadingUid(file.uid);
      const hide = message.loading("正在准备下载...", 0);
      try {
        await openOssFile(ossFile, "attachment");
      } finally {
        hide();
        setActionLoadingUid(null);
      }
    },
    [actionLoadingUid],
  );

  return (
    <div className={cn(styles.root, fill && styles.rootFill, className)}>
      <Spin
        spinning={isBusy}
        tip={isUploading ? "上传中..." : "处理中..."}
        wrapperClassName={styles.spinWrap}
      >
        <Dragger
          className={cn(
            styles.dragger,
            variant === "compact" && styles.variantCompact,
            hideDraggerWhenFull && reachMaxCount && styles.hideDraggerWhenFull,
          )}
          accept={accept}
          disabled={disabled || isBusy}
          multiple={!isSingleMode}
          maxCount={draggerMaxCount}
          fileList={displayFileList}
          showUploadList={{
            showPreviewIcon: true,
            showDownloadIcon: true,
            showRemoveIcon: !disabled && !isUploading,
          }}
          customRequest={customRequest}
          beforeUpload={beforeUpload}
          onPreview={handlePreview}
          onDownload={handleDownload}
          onRemove={handleRemove}
        >
          {showDropContent && !isUploading && (
            <div className={styles.dropContent}>
              <UploadIcon className="w-5 h-5 text-slate-400" />
              <span className={styles.dropHint}>{dragHint}</span>
              <span className={styles.formatHint}>{formatHint}</span>
            </div>
          )}
          {showDropContent && isUploading && (
            <div className={styles.dropContent}>
              <span className={styles.dropHint}>文件上传中，请稍候...</span>
            </div>
          )}
        </Dragger>
      </Spin>
    </div>
  );
};

export type { UploadFileChangeHandler, UploadFileProps } from "./types";
export default UploadFile;
